import { VISUALIZATION_TOKENS } from "@/config/visualizationTokens";
import React from "react";
import type { ComponentType } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import wNumb from "wnumb";
import { Icon } from "@mui/material";
import { getSubdomain as getSubdomainFromTldts } from "tldts";

import { S3_BUCKET_URL } from "../config/appConfig";
import { STATIC_IMAGES } from "./staticImages";
import { toastr } from "react-redux-toastr";
import RpPopover from "../components/popover/RpPopover";
import { logger } from "./loggerHelper";
import store from "../config/store";
import { CAMPAIGN_CONST, USER_ROLE } from "../config/constants";
import RpSvgIcon from "../components/icons/RpSvgIcon";
import { DIRECT_MAIL_CAMP_PROVIDER } from "../features/campaigns/direct-mail/utils/directMailConstants";
import { getMonthBusinessDays } from "./momentBusinessDays";
import { sanitizeIframeHTML } from "./sanitizeHelper";

type AnyRecord = Record<string, any>;
type NumberFormatOptions = Record<string, any>;

const LinkComponent = Link as ComponentType<any>;

/**
 * @method RPNumberFormat
 * @description return formatted number
 * @param {decimals: 2, prefix: "", suffix: "", thousand: ","} options
 */
export const RPNumberFormat = (
  options: NumberFormatOptions = { prefix: "", suffix: "", thousand: "," }
) => wNumb({ thousand: ",", ...options });

/**
 * @method getImage
 * @description return image
 * @param {*} image
 */
export const getImage = (image, type = "") => {
  if (image) {
    return `${S3_BUCKET_URL}${image}`;
  } else {
    switch (type) {
      case "PROFILE":
        return `/img/user.png`;
      case "BRAND":
        return `/img/dummyLogo.png`;
      case "COMPANY":
        return `/img/dummyLogo.png`;
      case "CREATIVE":
        return `/img/creative-img.png`;
      case "IMG":
        return `/img/img.png`;
      default:
        return `/img/user.png`;
    }
  }
};

/**
 * @method getFallbackImage
 * @description return fallback image
 * @param {*} image
 */
export const getFallbackImage = (type = "") => {
  switch (type) {
    case "PROFILE":
      return `/img/user.png`;
    case "BRAND":
      return `/img/dummyLogo.png`;
    case "COMPANY":
      return `/img/dummyLogo.png`;
    case "CREATIVE":
      return `/img/creative-img.png`;
    case "HYPER_PLACEHOLDER":
      return `/img/hyper_placeholder.png`;
    default:
      return `/img/user.png`;
  }
};

/**
 * @method getProfileImage
 * @description return profile image
 * @param {*} image
 */
export const getProfileImage = (image) => {
  return `${S3_BUCKET_URL}${image}`;
};

/**
 * @method getCampaignStatusText
 * @description return campaign status text
 * @param {*} status
 */
export const getCampaignStatusText = (status) => {
  switch (status) {
    case CAMPAIGN_CONST.STATUS.ACTIVE.ID:
      return CAMPAIGN_CONST.STATUS.ACTIVE.TEXT; // Active
    case CAMPAIGN_CONST.STATUS.SCHEDULED.ID:
      return CAMPAIGN_CONST.STATUS.SCHEDULED.TEXT;
    case CAMPAIGN_CONST.STATUS.COMPLETED.ID:
      return CAMPAIGN_CONST.STATUS.COMPLETED.TEXT;
    case CAMPAIGN_CONST.STATUS.PAUSED.ID:
      return CAMPAIGN_CONST.STATUS.PAUSED.TEXT;
    case CAMPAIGN_CONST.STATUS.DRAFT.ID:
      return CAMPAIGN_CONST.STATUS.DRAFT.TEXT;
    case CAMPAIGN_CONST.STATUS.DELIVERED.ID:
      return CAMPAIGN_CONST.STATUS.DELIVERED.TEXT;
    default:
      return CAMPAIGN_CONST.STATUS.INACTIVE.TEXT;
  }
};

/**
 * @method getBase64
 * @description return base64 of the provided file
 * @param {*} file
 */
export const getBase64 = (file): Promise<string> => {
  return new Promise((resolve, rejects) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      return resolve(String(reader.result || ""));
    };
    reader.onerror = function (error) {
      return rejects(error);
    };
  });
};

/**
 * @method getYouTubeVideoId
 * @description extract id from any yu tube url
 * @param {*} url
 */
export const getYouTubeVideoId = (url) => {
  // const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const regExp = /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#\&\?]*).*/;

  const match = url.match(regExp);
  // return match && match[7].length === 11 ? match[7] : false;
  return match && match[1].length === 11 ? match[1] : false;
};

/**
 * @method isYouTubeUrl
 * @description return true for valid YouTube url
 * @param {*} ulr
 */
export const isYouTubeUrl = (url) =>
  /^(https?\:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/.test(url);

/**
 * @method extractVideoIdFromUrl
 * @description return youtube videoId for valid YouTube url
 * @param {*} url
 */
export function extractVideoIdFromUrl(url) {
  const match = url.match(
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/(watch\?v=|embed\/|v\/)?([a-zA-Z0-9_-]{11})/
  );
  return match && match[5] ? match[5] : null;
}

/**
 * @method parseJson
 * @description return parsed json object
 * @param {*} jsonString
 */
export const parseJson = (jsonString) => {
  if (!jsonString) return null;
  try {
    return JSON.parse(jsonString);
  } catch (err) {
    logger.error("Error in parseJson", { err });
  }
};

/**
 * @method getFormatDate
 * @description return date in a format
 * @param {*} date
 * @param {String} format
 */
export const getFormatDate = (date, format = "MMM DD, YYYY") => {
  return date && moment(date).isValid() ? moment(date).format(format) : "";
};

/**
 * @method getMonthLabels
 * @description return month labels
 */
export const getMonthLabels = (start = 0) => {
  const labels = [
    { label: "Jan", value: 1 },
    { label: "Feb", value: 2 },
    { label: "Mar", value: 3 },
    { label: "Apr", value: 4 },
    { label: "May", value: 5 },
    { label: "Jun", value: 6 },
    { label: "Jul", value: 7 },
    { label: "Aug", value: 8 },
    { label: "Sep", value: 9 },
    { label: "Oct", value: 10 },
    { label: "Nov", value: 11 },
    { label: "Dec", value: 12 },
  ];

  if (start !== 0) {
    let a = labels.slice(start);
    let b = labels.slice(0, start);
    return [...a, ...b];
  }

  return labels;
};

/**
 * @method getBrandContributionAmount
 * @description return brand contribution. Pass last parameter false for non-format value
 * @param {*} val
 */
export const getBrandContributionAmount = (
  val,
  contribution_percentage,
  contribution_max_limit,
  brand_contribution = 1,
  format = true
) => {
  if (brand_contribution === 0) {
    return;
  }

  let cont: string | number = 0;

  if (contribution_max_limit && contribution_percentage) {
    const brandPercent = (val * 1 * contribution_percentage) / 100;
    cont =
      contribution_max_limit < brandPercent
        ? contribution_max_limit.toFixed(2)
        : brandPercent.toFixed(2);
  } else if (contribution_percentage) {
    cont = ((val * 1 * contribution_percentage) / 100).toFixed(2);
  } else if (contribution_max_limit) {
    cont = contribution_max_limit.toFixed(2);
  }
  if (format) {
    return RPNumberFormat({ prefix: "$", decimals: 2 }).to(
      parseFloat(String(cont))
    );
  }
  return parseFloat(String(cont));
};

/**
 * @method getChannels
 * @description return channels name
 */
export const getChannels = (package_channels) => {
  return package_channels
    ? uniq(package_channels.map((c) => c?.channel?.channel_name)).join(", ")
    : "There are none right now";
};

/**
 * @method getChannelIcons
 * @description return icons images tag for the given channels
 * @param {[]} channels
 * @returns
 */
export function getChannelIcons(channels = []) {
  if (!channels || channels.length === 0) return "";
  if (!Array.isArray(channels)) {
    return "";
  }

  const images = uniq(channels)
    .map((ch) => {
      return { name: ch, image: STATIC_IMAGES[ch.toLowerCase()] };
    })
    .filter((m) => m);

  const popContent = images.slice(3);

  const getIcon = (images) => {
    return (
      <div className="">
        {images.length
          ? images.map((m) => (
              <img
                src={m.image}
                alt={m.name}
                title={m.name}
                className="channel_image"
                width="20"
                style={{
                  maxWidth: 20,
                  margin: "1.5px",
                  borderRadius: "var(--radius-control)",
                }}
                key={m.name}
              />
            ))
          : ""}
      </div>
    );
  };

  return (
    <div className="d-flex align-items-center flex-wrap">
      {getIcon(images.slice(0, 3))}
      {images.length > 3 && (
        <RpPopover popContent={popContent.length ? getIcon(popContent) : null}>
          <small className="cursor-p" style={{ marginLeft: 2 }}>
            <strong>(+{popContent.length})</strong>
          </small>
        </RpPopover>
      )}
    </div>
  );

  // return (
  //   <div className="d-flex align-items-center flex-wrap">
  //     {images.length
  //       ? images.map((m) => (
  //           <img
  //             src={m.image}
  //             alt={m.name}
  //             title={m.name}
  //             className="img-fluid channel_image"
  //             width="16"
  //             height="16"
  //             style={{ maxWidth: 16, maxHeight: 16, margin: "2px" }}
  //             key={m.name}
  //           />
  //         ))
  //       : ""}
  //   </div>
  // );
}

export const processAndSortChannels = (
  channels: AnyRecord[] = []
): AnyRecord[] => {
  if (!Array.isArray(channels) || channels.length === 0) return [];

  return channels
    .map((channel) => {
      // Use same logic as getChannelIcons but return image path instead of JSX
      let image = channel.image;
      if ((!image || typeof image === "number") && channel.name) {
        const imageKey = channel.name.toLowerCase().replace(/\s+/g, " ");
        image = STATIC_IMAGES[imageKey] || null;
      }

      return {
        ...channel,
        image,
        grayscale:
          channel.grayscale ??
          (channel.status === "inactive" || channel.status === false),
        altText: channel.altText || channel.name || `Channel ${channel.id}`,
      };
    })
    .sort((a: AnyRecord, b: AnyRecord) => {
      // Sort by status: active first
      const aActive = a.status === "active" || a.status === true;
      const bActive = b.status === "active" || b.status === true;
      if (aActive !== bActive) return Number(bActive) - Number(aActive);

      // Then sort by name (case-insensitive)
      return (a.name || "")
        .toLowerCase()
        .localeCompare((b.name || "").toLowerCase());
    });
};

export const sortCommaSeparatedString = (str) => {
  if (!str || typeof str !== "string") return str;
  return str
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .join(", ");
};

// Channel color mapping
export const getChannelColor = (channelName) => {
  return (
    VISUALIZATION_TOKENS.channels[channelName] ||
    VISUALIZATION_TOKENS.channels.Ecomm
  );
};
/**
 * @method canJoinCampaign
 * @description return boolean to decide whether participant can join a campaign or not
 * @param {*} campaign
 */
export const canJoinCampaign = (campaign) => {
  if (!campaign) return false;
  if (
    campaign?.local_selection === 1 ||
    (campaign?.local_selection === 0 &&
      campaign?.budget_type === CAMPAIGN_CONST.BUDGET_TYPE.LOCATION_SPECIFIC)
  ) {
    if (
      campaign?.status === CAMPAIGN_CONST.STATUS.ACTIVE.ID ||
      campaign?.status === CAMPAIGN_CONST.STATUS.SCHEDULED.ID
    ) {
      if (
        campaign?.participant_campaign &&
        campaign?.budget_type !== CAMPAIGN_CONST.BUDGET_TYPE.PRESET
      ) {
        return campaign?.participant_campaign.is_draft === true ? true : false;
      } else {
        return true;
      }
    } else if (
      campaign?.campaign_type === CAMPAIGN_CONST.TYPE.NORMAL ||
      (campaign?.campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN &&
        campaign?.evergreen_available_after_completion_flag)
    ) {
      return true;
    }
  }
  return false;
};

/**
 * @method getTwoDecimalDigit
 * @description return up to d digits after decimal without rounding
 * @param {*} n
 * @param {*} d
 */
export const getTwoDecimalDigit = (n, d = 2) => {
  let str = `${n}`.split(".");
  if (str[1]) {
    let dec = str[1].slice(0, d);
    let j = `${str[0]}.${dec}`;
    return parseFloat(j);
  }
  return n;
};

/**
 * @method formateKeyName
 * @description return formatted key name
 * @param {*} key
 */
export const formateKeyName = (key = "") => {
  return key.replace(/_/g, " ");
};

/**
 * @method getFormattedData
 * @description return formatted data
 * @param {*} data
 * @param {*} key
 */
export const getFormattedData = (data, key = "", formats = null) => {
  if (formats && key && formats[key]) {
    return formats[key].prefix + data + formats[key].suffix;
  }
  return data;
};

const collectHiddenKeys = (value, hiddenKeys) => {
  if (!value) return;

  if (Array.isArray(value)) {
    value.forEach((item) => collectHiddenKeys(item, hiddenKeys));
    return;
  }

  if (typeof value === "string") {
    value
      .split(",")
      .map((key) => key.trim())
      .filter(Boolean)
      .forEach((key) => hiddenKeys.add(key));
    return;
  }

  if (typeof value === "object") {
    Object.entries(value).forEach(([key, isHidden]) => {
      if (isHidden) {
        hiddenKeys.add(key);
      }
    });
  }
};

export const getHiddenKeys = (...configs) => {
  const hiddenKeys = new Set();
  configs.forEach((config) => collectHiddenKeys(config, hiddenKeys));
  return Array.from(hiddenKeys);
};

export const getWidgetHiddenKeys = (widget = null, fallbackHiddenKeys = []) => {
  const apiHiddenKeys =
    widget?.hide_keys &&
    typeof widget.hide_keys === "object" &&
    !Array.isArray(widget.hide_keys)
      ? widget.hide_keys
      : null;

  return getHiddenKeys(apiHiddenKeys, fallbackHiddenKeys);
};

/**
 * @method getChartColors
 * @description return chart color set
 * @param {*} set
 */
export const getChartColors = (set = 1) => {
  return [
    ...(VISUALIZATION_TOKENS.series[set] || VISUALIZATION_TOKENS.series[1]),
  ];
};

export const joinByDateFormatHelper = (campaign: AnyRecord = {}) => {
  const { start_date } = campaign;
  return {
    joinByDate: function (format = "MMM DD, YYYY") {
      let resultDate = "";
      const parsedDate = moment(start_date);
      resultDate = parsedDate.subtract(6, "weeks").format("YYYY-MM-DD");
      return resultDate ? (
        <>
          {moment(resultDate).isValid() ? (
            <Icon
              component={RpSvgIcon}
              name="campaign_calender"
              color="primary"
              fontSize="small"
            />
          ) : null}
          {getFormatDate(resultDate, format)}
        </>
      ) : (
        "N/A"
      );
    },
  };
};
/**
 * @method campaignFormat
 * @description format campaign data
 * @param {*} campaign
 */
export const campaignFormatHelper = (campaign: AnyRecord = {}) => {
  const {
    id,
    campaign_name,
    status,
    system_generated_campaign_id,
    campaign_type,
    participant_campaign,
    start_date,
    end_date,
    channel_name,
    is_previous_record_available,
    participant_template,
    brand_owner_template,
    parent_company_template,
    availability_window,
    availability_window_date,
    getCampaignProvider,
    locations = [],
  } = campaign;

  return {
    isJoined: function () {
      return participant_campaign && participant_campaign.is_draft === false
        ? true
        : false;
    },
    name: function (url = `/dashboard/digital-campaign-report`) {
      const isLaunchDirectMailRoute =
        window?.location?.pathname.includes("launch-direct-mail");
      const isTemplateExists =
        (store.getState().user.role === USER_ROLE.PARTICIPANT.NAME &&
          participant_template) ||
        ((store.getState().user.role === USER_ROLE.BRAND_OWNER.NAME ||
          store.getState().user.role === USER_ROLE.BRAND_USER.NAME) &&
          brand_owner_template) ||
        store.getState().user.role === USER_ROLE.PARENT_COMPANY.NAME ||
        store.getState().user.role === USER_ROLE.PARENT_COMPANY_USER.NAME ||
        (store.getState().user.role ===
          USER_ROLE.PARENT_COMPANY_SUB_USER.NAME &&
          parent_company_template);
      const provider = getCampaignProvider;
      const showLink =
        provider !== DIRECT_MAIL_CAMP_PROVIDER.RRD
          ? isTemplateExists &&
            (is_previous_record_available ||
              (this.isJoined() &&
                (status === CAMPAIGN_CONST.STATUS.ACTIVE.ID ||
                  status === CAMPAIGN_CONST.STATUS.SCHEDULED.ID ||
                  status === CAMPAIGN_CONST.STATUS.COMPLETED.ID ||
                  status === CAMPAIGN_CONST.STATUS.PAUSED.ID)))
          : !isLaunchDirectMailRoute &&
            participant_template !== null &&
            participant_template !== undefined &&
            locations?.length > 0;

      return showLink ? (
        <LinkComponent
          to={{
            pathname: `${url}/${system_generated_campaign_id}`,
            state: {
              campaignId: id,
              campaignSysId: system_generated_campaign_id,
              from: "/dashboard",
            },
          }}
          className={`title-val campaign-name text-break ${
            showLink ? "main link" : ""
          }`}
          title={campaign_name}
        >
          {campaign_name}
        </LinkComponent>
      ) : (
        <span
          className={`title-val campaign-name text-break`}
          title={campaign_name}
        >
          {campaign_name}
        </span>
      );
    },
    status: function () {
      let className = "title-val";
      let campaignStatus = status;

      if (this.isJoined()) {
        // campaign joined
        campaignStatus = participant_campaign.status;

        if (participant_campaign.status === CAMPAIGN_CONST.STATUS.ACTIVE.ID) {
          className = "title-val status-active";
        }
      } else {
        // campaign not joined
        campaignStatus = status;
        if (status === CAMPAIGN_CONST.STATUS.ACTIVE.ID) {
          className = "title-val status-active";
        }
      }

      return (
        <span className={className}>
          {getCampaignStatusText(campaignStatus)}
        </span>
      );
    },
    startDate: function (format = "MMM DD, YYYY") {
      let startDate = "";
      if (this.isJoined()) {
        // campaign joined
        if (campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN) {
          startDate = participant_campaign.start_date;
        } else {
          startDate = participant_campaign.start_date;
        }
      } else {
        // campaign not joined
        if (campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN) {
          startDate = CAMPAIGN_CONST.TYPE.EVERGREEN;
        } else {
          startDate = start_date;
        }
      }

      if (startDate === CAMPAIGN_CONST.TYPE.EVERGREEN) {
        if (availability_window) {
          if (
            availability_window !==
            CAMPAIGN_CONST.AVAILABILITY_WINDOW.TYPE.ALWAYS_AVAILABLE
          ) {
            startDate = availability_window_date?.window_start_date;
          }
        } else {
          return "TBD";
        }
      }
      return startDate ? (
        <>
          {moment(startDate).isValid() ? (
            <Icon
              component={RpSvgIcon}
              name="campaign_calender"
              color="primary"
              fontSize="small"
            />
          ) : null}
          {getFormatDate(startDate, format)}
        </>
      ) : (
        "N/A"
      );
    },

    endDate: function (format = "MMM DD, YYYY") {
      let endDate = "";
      if (this.isJoined()) {
        // campaign joined
        if (campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN) {
          endDate = participant_campaign.end_date;
        } else {
          endDate = participant_campaign.end_date;
        }
      } else {
        // campaign not joined
        if (campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN) {
          endDate = CAMPAIGN_CONST.TYPE.EVERGREEN;
        } else {
          endDate = end_date;
        }
      }

      if (endDate === CAMPAIGN_CONST.TYPE.EVERGREEN) {
        if (availability_window) {
          if (
            availability_window !==
            CAMPAIGN_CONST.AVAILABILITY_WINDOW.TYPE.ALWAYS_AVAILABLE
          ) {
            endDate = availability_window_date?.last_day_to_join;
          }
        } else {
          return "TBD";
        }
      }

      return endDate ? (
        <>
          {moment(endDate).isValid() ? (
            <Icon
              component={RpSvgIcon}
              name="campaign_calender"
              color="primary"
              fontSize="small"
            />
          ) : null}
          {getFormatDate(endDate, format)}
        </>
      ) : (
        "N/A"
      );
    },
    getChannelIcons: function () {
      if (!channel_name) return "";
      const chArr = channel_name.split(",").sort();
      return getChannelIcons(chArr);
    },
    getChannelNames: function () {
      if (!channel_name) return "";
      const formatted = channel_name.replace(/,/g, ", ");
      return formatted;
    },
  };
};

/**
 * @method getContrastBWColor
 * @param {*} hexcolor
 */
export const getContrastBWColor = (hexcolor) => {
  // If a leading # is provided, remove it
  if (hexcolor.slice(0, 1) === "#") {
    hexcolor = hexcolor.slice(1);
  }

  // If a three-character hexcode, make six-character
  if (hexcolor.length === 3) {
    hexcolor = hexcolor
      .split("")
      .map(function (hex) {
        return hex + hex;
      })
      .join("");
  }

  // Convert to RGB value
  var r = parseInt(hexcolor.substr(0, 2), 16);
  var g = parseInt(hexcolor.substr(2, 2), 16);
  var b = parseInt(hexcolor.substr(4, 2), 16);

  // Get YIQ ratio
  var yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // Check contrast
  // return yiq >= 128 ? "black" : "white";
  return yiq >= 140 ? "black" : "white";
};

/**
 * @method getFormattedCampaignDate
 * @description return formatted campaign date
 * @param {*} campaign
 * @param {*} otherData
 * @returns
 */
export const getFormattedCampaignDate = (campaign) => {
  if (!campaign) return "";
  if (campaign?.start_date && campaign?.end_date) {
    return `${getFormatDate(campaign?.start_date)} - ${getFormatDate(
      campaign?.end_date
    )}`;
  }
  if (campaign?.campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN) {
    return CAMPAIGN_CONST.TYPE.EVERGREEN;
  } else {
    return "N/A";
  }
};

export const getFormattedCampaignStartDate = (campaign) => {
  if (!campaign) return "";
  if (campaign?.start_date) {
    return `${getFormatDate(campaign?.start_date)}`;
  } else if (campaign?.campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN) {
    return CAMPAIGN_CONST.TYPE.EVERGREEN;
  } else {
    return "N/A";
  }
};

export const getFormattedCampaignEndDate = (campaign) => {
  if (!campaign) return "";
  if (campaign?.end_date) {
    return `${getFormatDate(campaign?.end_date)}`;
  } else if (campaign?.campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN) {
    return CAMPAIGN_CONST.TYPE.EVERGREEN;
  } else {
    return "N/A";
  }
};

/**
 * @method hasUniqDataBy
 * @description check is the array of object has uniq data by the given field
 * @param {*} data
 * @param {*} by
 * @returns
 */
export const hasUniqDataBy = (data, by = "") => {
  return uniqBy(data, by).length === data.length;
};

/**
 * @method makeUniqChartData
 * @description make the chart data uniq by the given field
 * @param {*} data
 * @param {*} by
 * @returns
 */
export const makeUniqChartData = (data, by) => {
  if (!by) return data;
  if (hasUniqDataBy(data, by)) {
    return data;
  }
  const doneFor = [];
  return data.map((d) => {
    if (
      data.filter((o) => o[by] === d[by]).length > 1 &&
      !doneFor.includes(d[by])
    ) {
      doneFor.push(d[by]);
      const cp = { ...d };
      delete cp[by];
      const newData = { [by]: d[by] + " ", ...cp };
      return newData;
    }
    return d;
  });
};

export const getLocationSubscriptionStatus = (loc) => {
  const subscriptionData = loc?.subscriptionData[0];

  if (!subscriptionData) return 0;

  if (!subscriptionData?.isSubscribed) {
    return 0;
  } else if (subscriptionData?.isFallOut) {
    return -1;
  } else {
    return 1;
  }
};

export const isAllParticipantLocationsSubscribed = (locs) => {
  return locs.every((loc) => {
    const subscriptionData = loc?.subscriptionData.find(
      (d) =>
        d.system_generated_location_ids === loc.system_generated_location_id
    );
    if (!subscriptionData) return false;
    if (!subscriptionData?.isSubscribed || subscriptionData?.isFallOut) {
      return false;
    }
    return true;
  });
};

export const isAllParticipantLocationsNotSubscribed = (locs) => {
  return locs.every((loc) => {
    const subscriptionData = loc?.subscriptionData.find(
      (d) =>
        d.system_generated_location_ids === loc.system_generated_location_id
    );
    if (!subscriptionData) return true;
    if (!subscriptionData?.isSubscribed || subscriptionData?.isFallOut) {
      return true;
    }
    return false;
  });
};

export const getParticipantSubscribedLocationIds = (locs) => {
  return locs
    .filter((loc) => {
      const subscriptionData = loc?.subscriptionData.find(
        (d) =>
          d.system_generated_location_ids === loc.system_generated_location_id
      );
      if (!subscriptionData) return false;
      if (!subscriptionData?.isSubscribed || subscriptionData?.isFallOut) {
        return false;
      }
      return true;
    })
    .map((l) => l.subscriptionData[0].fk_location_id);
};

export const isThereAnyLocationUnsubscribed = (locs) => {
  return locs.some((loc) => {
    const subscriptionData = loc?.subscriptionData.find(
      (d) =>
        d.system_generated_location_ids === loc.system_generated_location_id
    );
    if (!subscriptionData) return true;
    if (!subscriptionData?.isSubscribed) {
      return true;
    }
    return false;
  });
};

export const isCampaignFallOutsideAnyLocationSubscription = (locs) => {
  return locs.some((loc) => {
    const subscriptionData = loc?.subscriptionData.find(
      (d) =>
        d.system_generated_location_ids === loc.system_generated_location_id
    );
    // if (!subscriptionData) return true;

    // subscribed but subscription end before campaign end date
    if (subscriptionData?.isSubscribed && subscriptionData?.isFallOut) {
      return true;
    }

    return false;
  });
};

export const RpConfirmDialog = (
  message = "",
  onOk = () => {},
  onCancel = () => {},
  id = "rp-confirm-dialog"
) => {
  const toastrConfirmOptions = {
    onOk: onOk,
    onCancel: onCancel,
    okText: "Yes",
    cancelText: "No",
    id,
  };
  toastr.confirm(message, toastrConfirmOptions);
};

/**
 * @method getTotalSpendSum
 * @description return total of total spend
 * @param {*} widget
 * @returns
 */
export const getTotalSpendSum = (widget) => {
  if (!widget?.data?.[0]) {
    return null;
  }

  const data = widget.data;
  const formats = widget.format;

  if (!data || data.length === 0) return "$0";

  // Check if it's multi-brand data structure (has system_generated_brand_id or fk_brand_id)
  if (
    Array.isArray(data) &&
    data.length > 0 &&
    data[0] &&
    (data[0].system_generated_brand_id || data[0].fk_brand_id)
  ) {
    // Multi-brand data structure
    let total = 0;
    data.forEach((brandData) => {
      if (brandData && brandData.data && Array.isArray(brandData.data)) {
        brandData.data.forEach((item) => {
          if (item && item.monthly_total_spend_brand) {
            total += parseFloat(item.monthly_total_spend_brand) || 0;
          }
        });
      }
    });

    return RPNumberFormat({
      prefix:
        formats && formats.monthly_total_spend_brand
          ? formats.monthly_total_spend_brand.prefix
          : "$",
      suffix:
        formats && formats.monthly_total_spend_brand
          ? formats.monthly_total_spend_brand.suffix
          : "",
      decimals: 2,
    }).to(total);
  } else {
    // Single brand data structure
    const labels = Object.keys(widget.data[0]);
    return RPNumberFormat({
      prefix: formats && formats[labels[1]] ? formats[labels[1]].prefix : "",
      suffix: formats && formats[labels[1]] ? formats[labels[1]].suffix : "",
      decimals: 2,
    }).to(
      data.reduce(
        (t, d) =>
          d[labels[1]] && parseFloat(d[labels[1]])
            ? t + parseFloat(d[labels[1]])
            : t,
        0
      )
    );
  }
};

/**
 * @method isInLastNBusinessDay
 * @description return if given date is in last N business days or after the last business day
 * @param {*} date
 * @param {*} days
 * @returns {boolean|null}
 */
export function isInLastNBusinessDay(date, days = 4) {
  if (!date) return null;
  const toDay = moment(date);

  if (!toDay.isValid()) return null;
  const monthBusinessDays = getMonthBusinessDays(toDay);

  const lastNBusinessDays = monthBusinessDays.slice(-days);
  const formattedDates = lastNBusinessDays.map((d) => d.format("YYYY-MM-DD"));

  const firstBusinessDay = moment(lastNBusinessDays[0]);
  const lastMonthDay = moment(date).endOf("month");

  const isBetween =
    toDay.isSameOrAfter(firstBusinessDay) &&
    toDay.isSameOrBefore(lastMonthDay, "day");

  return formattedDates.includes(toDay.format("YYYY-MM-DD")) || isBetween;
}

/**
 * @method isTodayBetweenDates
 * @description return boolean if today is between the given dates
 * @param {*} startDate
 * @param {*} endDate
 * @returns
 */
export function isTodayBetweenDates(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const start = moment(startDate);
  const end = moment(endDate);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  return moment().isBetween(start, end);
}

/**
 * @method downloadFileAnchorTag
 * @description create anchor tag and set file and download file
 * @param {*} filename
 * @param {*} url
 */
export function downloadFileAnchorTag(url, filename = "file") {
  const anchorEl = document.createElement("a");
  anchorEl.setAttribute("href", url);
  anchorEl.setAttribute("download", filename);
  anchorEl.style.display = "none";
  document.body.appendChild(anchorEl);
  anchorEl.click();
  document.body.removeChild(anchorEl);
}

/**
 * @method makeFirstCharCap
 * @description make first char capital and return
 * @param {*} str
 * @returns
 */
export function makeFirstCharCap(str = "") {
  return str && typeof str === "string"
    ? str.charAt(0)?.toUpperCase() + str.slice(1)
    : str;
}

export function capitalizeEachWord(str = "") {
  if (typeof str !== "string") return str;

  return str
    .split(" ")
    .map((word) =>
      word
        .split("'")
        .map(
          (part) => part.charAt(0)?.toUpperCase() + part.slice(1)?.toLowerCase()
        )
        .join("'")
    )
    .join(" ");
}

/**
 * @method addEmptyOption
 * @description add an empty option as first element of array
 * @param {*} options
 * @param {*} label
 * @returns
 */
export function addEmptyOption(options = [], label = "Select") {
  const newOptions = [...options];
  newOptions.unshift({
    label: label,
    value: "",
  });
  return newOptions;
}

/**
 * @method getStatusCode
 * @description return status code based on status string
 * @param {*} status
 * @returns
 */
export function getStatusCode(status: string | number = "", altCode = "") {
  let statusCode = "";
  switch (status) {
    case 1:
    case "Active":
    case "active":
    case "Live":
    case "Sent":
      statusCode = "success";
      break;
    case 2:
    case "Scheduled":
    case "scheduled":
      statusCode = "warn";
      break;
    case 3:
    case "Completed":
    case "completed":
    case "draft":
      statusCode = "info";
      break;
    case 4:
    case "Inactive":
    case "inactive":
    case "Paused":
    case "paused":
      statusCode = altCode ? altCode : "error";
      break;
    case 6:
    case "Delivered":
    case "delivered":
      statusCode = "info";
      break;
    default:
      statusCode = "default";
  }
  return statusCode;
}

export function roundDownToClosestRangeValue(number, range) {
  if (typeof number !== "number" || isNaN(number)) {
    return 0;
  } else if (!range || !Array.isArray(range)) {
    return 0;
  }

  // Sort the range values in ascending order
  range.unshift(0);
  const sortedRange = range.sort((a, b) => a - b);

  // Find the closest lower value from the sorted range
  let closestLowerValue = sortedRange[0];
  for (const value of sortedRange) {
    if (value <= number) {
      closestLowerValue = value;
    } else {
      break; // Exit the loop when we find a value greater than the number
    }
  }

  return closestLowerValue;
}

export function roundUpToClosestRangeValue(number, range) {
  if (typeof number !== "number" || isNaN(number)) {
    return 0;
  } else if (!range || !Array.isArray(range)) {
    return 0;
  }

  // Sort the range values in ascending order
  range.unshift(0);
  const sortedRange = range.sort((a, b) => a - b);

  // Find the closest higher value from the sorted range
  let closestHigherValue = sortedRange[sortedRange.length - 1];
  for (let i = sortedRange.length - 1; i >= 0; i--) {
    if (sortedRange[i] >= number) {
      closestHigherValue = sortedRange[i];
    } else {
      break; // Exit the loop when we find a value less than the number
    }
  }

  return closestHigherValue;
}

/**
 * @constant isSafariBrowser
 * @description return true if current browser is safari
 *
 */
export const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(
  navigator?.userAgent
);

/**
 * @method addParamsToIframeSrc
 * @description add params to the iframe src
 * @param {*} iframeHtml
 * @param {*} paramsObject
 * @returns
 */
export const addParamsToIframeSrc = (
  iframeHtml,
  paramsObject: Record<string, string | number | boolean>
) => {
  if (!iframeHtml || typeof iframeHtml !== "string") {
    logger.warn("Invalid iframe HTML provided to addParamsToIframeSrc");
    return "";
  }

  if (
    !paramsObject ||
    typeof paramsObject !== "object" ||
    Array.isArray(paramsObject)
  ) {
    logger.warn("Invalid paramsObject provided to addParamsToIframeSrc");
    return iframeHtml;
  }

  const sanitizedHTML = sanitizeIframeHTML(iframeHtml);
  if (!sanitizedHTML) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHTML, "text/html");
  const iframeElement = doc.querySelector("iframe");

  if (!iframeElement) {
    logger.warn("No iframe element found");
    return sanitizedHTML;
  }

  const currentSrc = iframeElement.getAttribute("src");
  if (!currentSrc) {
    logger.warn("Invalid iframe src attribute");
    return sanitizedHTML;
  }

  let iframeSrcUrl;
  try {
    iframeSrcUrl = new URL(currentSrc, window.location.origin);
  } catch (error) {
    iframeSrcUrl = null;
  }

  const iframeHostname = iframeSrcUrl?.hostname?.toLowerCase();
  const isYouTubeIframe =
    iframeHostname === "youtube.com" ||
    iframeHostname === "www.youtube.com" ||
    iframeHostname === "youtu.be" ||
    iframeHostname === "www.youtu.be" ||
    iframeHostname === "youtube-nocookie.com" ||
    iframeHostname === "www.youtube-nocookie.com";

  const paramString = Object.entries(paramsObject)
    .map(
      ([key, value]) =>
        `p.${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join("&");

  const newSrc =
    currentSrc.trim() +
    (currentSrc.includes("#") ? "" : "#") +
    paramString.trim();

  iframeElement.setAttribute("src", newSrc);
  iframeElement.setAttribute("loading", "lazy");
  if (isYouTubeIframe && !iframeElement.getAttribute("referrerpolicy")) {
    iframeElement.setAttribute(
      "referrerpolicy",
      "strict-origin-when-cross-origin"
    );
  }

  return iframeElement.outerHTML;
};

/**
 * @method removeCampaignByNameLike
 * @description filter the campaigns by like campaign name
 */
export const removeCampaignByNameLike = (campaigns = [], names = []) => {
  if (!names?.length) return campaigns;
  const lowerCaseNames = names.map((name) => name.toLowerCase());
  return campaigns?.filter((c) => {
    if (typeof c?.campaign_name === "string") {
      const campaignWords = c.campaign_name.toLowerCase().split(" ");
      return !campaignWords.some((word) => lowerCaseNames.includes(word));
    }
    return true;
  });
};

/**
 * @method downloadByLink
 * @description create anchor tag and download file
 * @param {*} url
 */
export function downloadByLink(url) {
  if (!url) return;
  const anchorEl = document.createElement("a");
  anchorEl.href = url;
  anchorEl.download = "";
  anchorEl.style.display = "none";
  document.body.appendChild(anchorEl);
  anchorEl.click();
  document.body.removeChild(anchorEl);
}

/**
 * @method isTodayBetween24AndEndOfMonth
 * @description Checks if today is between the 25th of the current month and the end of the current month.
 * @return {boolean} True if today is between the 25th of the current month and the end of the current month, false otherwise.
 */
export const isTodayBetween25AndEndOfMonth = (today = moment()) => {
  const twentyFourth = moment(today).date(25);
  const endOfMonth = moment(today).endOf("month");
  return moment(today).isBetween(twentyFourth, endOfMonth, "day", "[]");
};

export const getHistoryTablesFormatDate = (date, format) => {
  return date && moment(date).isValid() ? moment(date).format(format) : "";
};

export const keyToName = (key = "") => {
  let str = key.replace(/_/g, " ");
  str = str
    ?.split(" ")
    ?.map((s) => `${s.charAt(0)?.toUpperCase()}${s.substr(1)}`)
    ?.join(" ");
  return str;
};

/**
 * Merges two arrays of objects, updating existing items with new data
 * based on a unique key while preserving the order
 * @param {Array} prevArray - Array of existing objects
 * @param {Array} newArray - Array of new objects to merge
 * @param {string} uniqueKey - Key to use for unique identification
 * @returns {Array} Merged array with updated data
 */
export const mergeArraysWithUniqueKey = (
  prevArray = [],
  newArray = [],
  uniqueKey
) => {
  const itemMap = new Map(prevArray.map((item) => [item[uniqueKey], item]));

  newArray.forEach((newItem) => {
    itemMap.set(newItem[uniqueKey], newItem);
  });

  return Array.from(itemMap.values());
};

export const urlRegex =
  /\.(csv|xlsx|zip|pdf|rar|jpg|jpeg|png|gif|psd|mp4|ai|doc)$/i;

// Helper to check if value contains URLs
export const containsUrls = (value) => {
  if (typeof value === "string") return urlRegex.test(value);
  if (Array.isArray(value)) {
    return value.some((item) =>
      typeof item === "object"
        ? urlRegex.test(item.file_url)
        : urlRegex.test(item)
    );
  }
  return false;
};

export function getSubdomain() {
  try {
    const host = window.location.hostname; // e.g., "sub.example.com"
    const subdomain = getSubdomainFromTldts(host, {
      validHosts: ["localhost"],
    });
    return subdomain;
  } catch (error) {
    logger.error("Error getting subdomain", error);
    return null;
  }
}

// Helper to chunk an array into subarrays of a given size
export const chunkArray = (arr, size) => {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );
};

//  Encodes a password using Base64 (btoa).
export const encodePassword = (password) => {
  try {
    return btoa(password);
  } catch (error) {
    logger.error("Error encoding password:", error);
    return "";
  }
};

export const getDashboardPath = () => {
  const loggedInUserRole = store?.getState()?.user?.role || null;
  return loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
    loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
    loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME
    ? "/overview-dashboard"
    : "/dashboard";
};

/**
 * @method generateMultiBrandEmbedParams
 * @description Generate embed widget parameters for multiple brands
 * @param {Array} brandIds - Array of brand system generated IDs
 * @param {Object} baseParams - Base parameters to include
 * @returns {Object} Object with multiple BrandID parameters
 */
export const generateMultiBrandEmbedParams = (
  brandIds = [],
  baseParams = {}
) => {
  const embedParams = { ...baseParams };

  if (brandIds && brandIds.length > 0) {
    brandIds.forEach((brandId, index) => {
      if (brandId) {
        // For the first brand, use the standard key
        // For subsequent brands, append index to make unique keys
        const key = index === 0 ? "BrandID" : `BrandID_${index + 1}`;
        embedParams[key] = brandId;
      }
    });
  }

  return embedParams;
};

// Generate embed widget parameters for multiple brands
export const getMultipleEmbedWidgetParams = (
  userBrands = [],
  CUSTOM_WIDGET_PARAM: AnyRecord = {}
) => {
  const brandIds =
    Array.isArray(userBrands) && userBrands.length > 0
      ? userBrands
          .map((brand) => brand?.system_generated_brand_id)
          .filter(Boolean)
      : [];

  if (brandIds.length === 0) return {};

  // Format for multiple BrandIDs
  const formatMultiBrandParam = (ids) =>
    ids
      .map((id, index) => `${index === 0 ? "BrandID" : "p.BrandID"}=${id}`)
      .join("&");

  return {
    [CUSTOM_WIDGET_PARAM?.BrandID || "BrandID"]:
      brandIds.length === 1 ? brandIds[0] : formatMultiBrandParam(brandIds),
  };
};

export function updateRowsWithBrands(rows = [], brandCount) {
  if (!brandCount || !Array.isArray(rows) || rows.length === 0) return rows;

  const firstRow = rows[0];
  const firstColumnRow = firstRow?.columnRows?.[0];

  if (!firstColumnRow || !Array.isArray(firstColumnRow.columns)) {
    return rows;
  }

  // Check if 'Brands' column already exists
  const brandColumnExists = firstColumnRow.columns.some(
    (col) => col?.widget?.name === "Brands"
  );

  if (brandColumnExists) {
    return rows; // Prevent duplication
  }

  const newColumn = {
    id: "brands-column-id", // Replace with uuid() if needed
    widget: {
      name: "Brands",
      widget_for: "parent_company",
      type: "stats",
      data: brandCount,
    },
    content: "Brands column",
    customName: "Brands",
  };

  // Clone the full structure to avoid mutation (React-safe)
  const updatedRows = [...rows];
  updatedRows[0] = { ...firstRow };
  updatedRows[0].columnRows = [...firstRow.columnRows];
  updatedRows[0].columnRows[0] = { ...firstColumnRow };
  updatedRows[0].columnRows[0].columns = [newColumn, ...firstColumnRow.columns];

  return updatedRows;
}

export const removeLicenseKeyWatermark = () => {
  const parents = document.querySelectorAll(".MuiDateRangeCalendar-root");
  if (parents && parents.length > 0) {
    parents.forEach((parent) => {
      const childDivs = parent.querySelectorAll("div");
      childDivs.forEach((div) => {
        if (div.textContent.includes("MUI X Missing license key")) {
          div.remove();
        }
      });
    });
  }
};

export const formattedAmount = (amount, local = "en-US", currency = "USD") =>
  new Intl.NumberFormat(local, {
    style: "currency",
    currency: currency,
  }).format(amount);

export const formattedNumber = (
  amount,
  {
    local = "en-US",
    decimal = 0,
    minDecimal = decimal,
    maxDecimal = decimal,
    useGrouping = true,
  } = {}
) =>
  new Intl.NumberFormat(local, {
    useGrouping,
    minimumFractionDigits: minDecimal,
    maximumFractionDigits: maxDecimal,
  }).format(amount);

export const formatDateForApiRequest = (date) => {
  if (!date) return null;

  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`; // → "2025-11-27"
};

export function downloadCSVFile(data, fileName, mimeType) {
  try {
    const blob =
      data instanceof Blob ? data : new Blob([data], { type: mimeType });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = fileName;
    a.style.display = "none";
    document.body.appendChild(a);

    a.click();

    URL.revokeObjectURL(url);
    a.remove();
  } catch (error) {
    logger.error("Error downloading CSV file:", error);
  }
}

/**
 * @method createDatePickerInputClickHandler
 * @description Helper function to handle input click events for MUI DatePicker components
 * This allows the calendar to open when clicking anywhere on the input field,
 * not just the icon button.
 */
export const createDatePickerInputClickHandler = (
  onOpen?: () => void,
  disabled = false,
  isOpen = false
) => {
  return (event?: { target?: Element | null }) => {
    if (disabled) return;
    const clickedElement = event && event?.target;
    const isIconButtonArea =
      clickedElement &&
      clickedElement?.closest?.(".MuiIconButton-root") !== null;
    if (!isIconButtonArea) {
      if (!isOpen && onOpen && typeof onOpen === "function") {
        onOpen();
      }
    }
  };
};

export const sanitizeSearchInput = (value) =>
  value.replace(/[<>{}()\[\]\\'";]/g, "").trim();
export const getCampaignLaunchBufferDays = () => {
  const brand = store.getState()?.brand?.selectedBrand;
  const campaignLaunchTimeline = Number(brand?.campaign_launch_timeline);
  return campaignLaunchTimeline > 0 ? campaignLaunchTimeline : 5;
};
